'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface Project {
  id: number;
  num: string;
  category: string;
  title: string;
  location: string;
  timeline: string;
  status: string;
  description: string;
  highlights: string[];
  partners: string;
  href: string;
  image: string;
  imageAlt: string;
}

const projects: Project[] = [
{
  id: 1,
  num: '01 / 05',
  category: 'Upstream',
  title: 'Tilenga Project',
  location: 'Buliisa & Nwoya Districts',
  timeline: '2025–2027',
  status: 'In Development',
  description: 'The Tilenga project involves the development of oil fields in the Lake Albert basin, targeting production of approximately 190,000 barrels of oil per day at peak. It includes drilling of over 400 wells and construction of a Central Processing Facility.',
  highlights: [
  '~190,000 barrels/day peak production',
  '400+ wells planned across 6 fields',
  'Central Processing Facility at Buliisa',
  'Operated by TotalEnergies EP Uganda'],

  partners: 'TotalEnergies EP Uganda (Operator), CNOOC Uganda, UNOC',
  href: '/projects/tilenga',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b10c3355-1778365790553.png",
  imageAlt: 'Tilenga oil project development site at Lake Albert basin Buliisa District Uganda upstream operations'
},
{
  id: 2,
  num: '02 / 05',
  category: 'Upstream',
  title: 'Kingfisher Development',
  location: 'Kikuube District',
  timeline: '2024–2026',
  status: 'In Development',
  description: 'The Kingfisher Development Area (KFDA) covers oil fields in the Lake Albert Rift Basin. CNOOC Uganda operates the project targeting approximately 40,000 barrels per day at peak production with 132 production wells.',
  highlights: [
  '~40,000 barrels/day peak production',
  '132 production wells planned',
  'Central Processing Facility at Kasenyi',
  'Operated by CNOOC Uganda'],

  partners: 'CNOOC Uganda (Operator), TotalEnergies EP Uganda, UNOC',
  href: '/projects',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b49d3bf7-1778365790966.png",
  imageAlt: 'Kingfisher oil field development area Lake Albert Rift Basin Kikuube District Uganda CNOOC operations'
},
{
  id: 3,
  num: '03 / 05',
  category: 'Midstream',
  title: 'East African Crude Oil Pipeline (EACOP)',
  location: 'Uganda – Tanzania',
  timeline: '2023–2025',
  status: 'Under Construction',
  description: 'The EACOP is a 1,443 km heated and buried crude oil pipeline running from Kabaale, Uganda to Tanga Port, Tanzania. It is the world\'s longest electrically heated crude oil pipeline, designed to transport 216,000 barrels per day.',
  highlights: [
  '1,443 km total pipeline length',
  '216,000 barrels/day capacity',
  'World\'s longest electrically heated pipeline',
  'Connects Uganda to Tanga Port, Tanzania'],

  partners: 'TotalEnergies (Operator), UNOC, CNOOC Uganda, TPDC Tanzania',
  href: '/projects/eacop',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b4431df-1778365790570.png",
  imageAlt: 'East African Crude Oil Pipeline EACOP construction spanning Uganda Tanzania midstream infrastructure'
},
{
  id: 4,
  num: '04 / 05',
  category: 'Midstream',
  title: 'Kabaale Industrial Park',
  location: 'Hoima District',
  timeline: '2022–2026',
  status: 'Active',
  description: 'The Kabaale Industrial Park serves as the hub for Uganda\'s oil and gas infrastructure. It hosts the refinery site, pipeline terminal, and associated industrial facilities, covering over 29 square kilometers.',
  highlights: [
  '29+ sq km industrial zone',
  'Pipeline terminal and storage',
  'Refinery site infrastructure',
  'Managed by UNOC'],

  partners: 'UNOC, Ministry of Energy & Mineral Development',
  href: '/projects',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18f718b8d-1778365790639.png",
  imageAlt: 'Kabaale Industrial Park oil and gas infrastructure hub Hoima District Uganda UNOC managed facility'
},
{
  id: 5,
  num: '05 / 05',
  category: 'Downstream',
  title: 'Uganda Oil Refinery',
  location: 'Kabaale, Hoima District',
  timeline: '2026–2030',
  status: 'Planning',
  description: 'The Uganda Oil Refinery will process crude oil from the Lake Albert fields into petroleum products for domestic consumption and regional export. The refinery is designed to process 60,000 barrels per day.',
  highlights: [
  '60,000 barrels/day processing capacity',
  'Full range of petroleum products',
  'Reduces Uganda\'s import dependency',
  'Anchors national energy security'],

  partners: 'UNOC, Government of Uganda, Strategic Investors',
  href: '/projects/refinery',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d6f6afc-1778365791624.png",
  imageAlt: 'Uganda Oil Refinery processing facility Kabaale Hoima District downstream operations national energy security'
}];


export default function FlagshipProjects() {
  const [activeProject, setActiveProject] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const project = projects[activeProject];

  return (
    <section ref={sectionRef} id="projects" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease'
          }}>
          
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 03 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]" style={{ fontFamily: 'Inter, sans-serif' }}>Major Initiatives</span>
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.025em' }}>
              
              Flagship <span className="text-[#CC0000]">Projects</span>
            </h2>
            <p className="text-lg text-gray-600 mt-5" style={{ fontFamily: 'Inter, sans-serif' }}>
              An overview of UNOC&apos;s flagship projects across the petroleum value chain.
            </p>
          </div>
          <Link
            href="/projects"
            className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
            
            View All Projects
            <span className="bg-white/15 group-hover:bg-white/25 transition-colors p-1.5 rounded-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 7h10v10" />
                <path d="M7 17 17 7" />
              </svg>
            </span>
          </Link>
        </div>

        {/* Projects layout */}
        <div
          className="grid lg:grid-cols-12 gap-0 border border-gray-200"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(20px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s'
          }}>
          
          {/* Project list */}
          <div className="lg:col-span-4 border-r border-gray-200">
            {projects.map((p, i) =>
            <button
              key={p.id}
              onClick={() => setActiveProject(i)}
              className={`w-full text-left flex items-center justify-between p-6 border-b border-gray-200 transition-colors group ${
              i === activeProject ?
              'bg-[#0F0F0F]' :
              'bg-white hover:bg-[#F8F9FA]'}`
              }>
              
                <div className="flex items-center gap-4">
                  <span
                  className={`font-mono text-xs tracking-[0.2em] ${
                  i === activeProject ? 'text-[#CC0000]' : 'text-gray-400'}`
                  }>
                  
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <span
                    className={`text-[10px] font-mono uppercase tracking-[0.15em] block mb-1 ${
                    i === activeProject ? 'text-[#CC0000]' : 'text-gray-400'}`
                    }>
                    
                      {p.category}
                    </span>
                    <h3
                    className={`text-sm font-bold leading-snug ${
                    i === activeProject ? 'text-white' : 'text-[#0F0F0F]'}`
                    }
                    style={{ fontFamily: 'Inter, sans-serif' }}>
                    
                      {p.title}
                    </h3>
                  </div>
                </div>
                <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`flex-shrink-0 ${
                i === activeProject ? 'text-[#CC0000]' : 'text-gray-300'}`
                }
                aria-hidden="true">
                
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            )}
          </div>

          {/* Project detail */}
          <div className="lg:col-span-8">
            <div className="relative aspect-[16/9] lg:aspect-auto lg:h-[340px] overflow-hidden">
              <img
                src={project.image}
                alt={project.imageAlt}
                className="w-full h-full object-cover"
                style={{ transition: 'opacity 0.4s ease' }} />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/80 via-[#0F0F0F]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-mono text-[10px] text-[#CC0000] tracking-[0.2em]">{project.num}</span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/70">{project.category}</span>
                  <span className="ml-auto text-[10px] font-mono text-white/50 bg-white/10 px-2 py-0.5 rounded-sm">{project.status}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{project.title}</h3>
              </div>
            </div>
            <div className="p-6 lg:p-8 bg-white">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-400 mb-1">Location</p>
                  <p className="text-sm font-semibold text-[#0F0F0F]">{project.location}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-gray-400 mb-1">Timeline</p>
                  <p className="text-sm font-semibold text-[#0F0F0F]">{project.timeline}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>{project.description}</p>
              <ul className="space-y-2 mb-6">
                {project.highlights.map((h, i) =>
                <li key={i} className="flex items-start gap-2 text-sm text-[#0F0F0F]">
                    <span className="text-[#CC0000] font-bold mt-0.5">→</span>
                    {h}
                  </li>
                )}
              </ul>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">{project.partners}</p>
                <Link
                  href={project.href}
                  className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-4 pr-2 py-2 rounded-sm text-xs font-semibold hover:bg-[#AA0000] transition-colors group">
                  
                  View Project
                  <span className="bg-white/15 group-hover:bg-white/25 p-1 rounded-sm transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 7h10v10" />
                      <path d="M7 17 17 7" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}